/**
 * The editor.
 *
 * Three jobs: describe a piece, hear it, export it. The controls are generated
 * from the field lists below rather than written out in HTML, so adding a
 * parameter is one line here instead of three edits that can disagree.
 *
 * Everything the page needs comes from `src/`. Instrument names, layer
 * defaults and the timing rules are imported rather than retyped, because a
 * second copy of any of them would drift and the editor would start describing
 * a piece the renderer does not build.
 */

import { INSTRUMENTS, INSTRUMENTS_BY_ROLE } from "../src/engine.js";
import {
  DEFAULTS, describePiece, type LayerRole, type Piece,
} from "../src/piece.js";
import { type Groove, GROOVE_NAMES, GROOVES } from "../src/grooves.js";
import {
  ALL_PROGRESSIONS, MOOD_FAMILIES, MOOD_NAMES, type Mood, moodFromPiece, randomPiece,
} from "../src/randomise.js";
import { MODE_NAMES, TONIC_NAMES } from "../src/theory.js";
import { encodeWav } from "../src/wav.js";
import { makeZip } from "../src/zip.js";
import { cleanName, deleteFeeling, loadFeelings, saveFeeling } from "./feelings.js";
import { changedLayers, needsRender } from "../src/changed.js";
import { creditLine, licenceText } from "../src/licence.js";
import { decodePiece, encodePiece } from "../src/share.js";
import { canNudge, nudge, type Verb } from "../src/nudge.js";
import { buildHelp } from "./help.js";
import { AdaptivePlayer } from "./player.js";
import { LANE_GAP, LANE_HEIGHT, Timeline } from "./timeline.js";
import type { RenderResponse } from "./worker.js";

// ---- the form ---------------------------------------------------------------

interface FieldSpec {
  key: string;
  label: string;
  type: "text" | "number" | "range" | "checkbox" | "select" | "datalist";
  value: string | number | boolean;
  min?: number;
  max?: number;
  step?: number;
  options?: string[];
  hint?: string;
}

/**
 * Nothing here needs typing.
 *
 * The key used to be a text box you could put anything in and find out it was
 * wrong several seconds into a render. Tonic and mode are closed sets, so they
 * are pickers, and a whole class of error stops being expressible. Tempo and
 * length are sliders because nobody has an exact number in mind for either.
 *
 * The progression stays typeable, because roman numerals are the one place
 * where the format is genuinely open and a list would cap what you can make.
 * It offers every progression the styles use, so picking is the default and
 * typing is the escape hatch.
 */
const PIECE_FIELDS: FieldSpec[] = [
  { key: "name", label: "Name", type: "text", value: "colony_day" },
  { key: "tonic", label: "Key", type: "select", value: "D", options: TONIC_NAMES },
  { key: "mode", label: "Mode", type: "select", value: "minor", options: MODE_NAMES },
  {
    key: "progression", label: "Chords", type: "datalist", value: "i - VI - III - VII",
    options: ALL_PROGRESSIONS,
    hint: "Roman numerals. Case matters: VI is major, vi is minor.",
  },
  { key: "tempo", label: "Tempo", type: "range", value: 82, min: 40, max: 180, step: 1 },
  { key: "duration", label: "Length (s)", type: "range", value: 72, min: 16, max: 300, step: 4 },
  {
    key: "barsPerChord", label: "Bars per chord", type: "select", value: "4",
    options: ["1", "2", "4", "8"],
    hint: "One bar a chord is a song. Four is ambient.",
  },
  {
    key: "space", label: "Room", type: "range", value: 0.28, min: 0, max: 0.85, step: 0.01,
    hint: "How much the piece echoes. Past about 0.6 the tail starts washing a beat away.",
  },
  { key: "seed", label: "Seed", type: "number", value: 1, min: 1, max: 999999, step: 1 },
  {
    key: "loop", label: "Loop seamlessly", type: "checkbox", value: true,
    hint: "Drops the fades and rounds the length to whole progression cycles.",
  },
];

const ROLES: LayerRole[] = ["pad", "bass", "rhythm", "melody", "perc"];

const ROLE_INSTRUMENT: Record<LayerRole, string> = {
  pad: "glass_pad",
  bass: "deep_bass",
  rhythm: "bell",
  melody: "celeste",
  // Percussion takes its instruments from the groove, so this is only what the
  // instrument dropdown opens on.
  perc: "kick",
};

function layerFields(role: LayerRole): FieldSpec[] {
  const d = DEFAULTS[role];
  // Every instrument stays selectable, but the ones that suit this layer come
  // first. A sub bass on the melody is a legitimate thing to want and a
  // terrible thing to be shown first.
  const suited = INSTRUMENTS_BY_ROLE[role] ?? [];
  const instruments = [...suited, ...Object.keys(INSTRUMENTS).filter((i) => !suited.includes(i))];
  // Percussion has no pitch, no density and no instrument of its own: the
  // groove decides all three. Showing those controls would be four sliders
  // that do nothing.
  if (role === "perc") {
    return [
      { key: "groove", label: "Groove", type: "select", value: d.groove, options: GROOVE_NAMES },
      { key: "volume", label: "Volume", type: "range", value: d.volume, min: 0, max: 0.4, step: 0.005 },
      { key: "enterAt", label: "Enters at", type: "range", value: d.enterAt, min: 0, max: 1, step: 0.05 },
    ];
  }

  const fields: FieldSpec[] = [
    { key: "instrument", label: "Instrument", type: "select", value: ROLE_INSTRUMENT[role], options: instruments },
    { key: "octave", label: "Octave", type: "number", value: d.octave, min: 1, max: 6, step: 1 },
    { key: "density", label: "Density", type: "range", value: d.density, min: 0, max: 1, step: 0.05 },
    { key: "volume", label: "Volume", type: "range", value: d.volume, min: 0, max: 0.3, step: 0.005 },
    { key: "enterAt", label: "Enters at", type: "range", value: d.enterAt, min: 0, max: 1, step: 0.05 },
  ];
  // Pad and bass hold chords and roots; they have no figure to repeat, so the
  // motif controls would be two dead sliders.
  if (role === "rhythm" || role === "melody") {
    fields.push(
      // This was missing, and its absence was a silent bug rather than a gap:
      // the form is what the renderer reads, so a setting with no control here
      // is dropped on the way back and falls to its default. Every arpeggio
      // style was quietly rendering phrases instead.
      {
        key: "figure", label: "Plays", type: "select", value: d.figure,
        options: ["motif", "arpeggio", "pulse"],
        hint: "Phrases that come back, a steady run through the chord, or one repeated note.",
      },
      { key: "motifs", label: "Figures", type: "number", value: d.motifs, min: 1, max: 8, step: 1,
        hint: "How many phrases it invents before repeating them. 1 is an ostinato." },
      { key: "variation", label: "Variation", type: "range", value: d.variation, min: 0, max: 1, step: 0.05,
        hint: "How much a phrase drifts on each repeat. Past about 0.4 it stops being a phrase." },
    );
  }
  return fields;
}

// ---- building the controls --------------------------------------------------

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K, cls?: string, text?: string,
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text !== undefined) node.textContent = text;
  return node;
};

function control(id: string, spec: FieldSpec): HTMLElement {
  const wrap = el("div", "field");
  const label = el("label", undefined, spec.label);
  label.htmlFor = id;

  let input: HTMLInputElement | HTMLSelectElement;
  if (spec.type === "select") {
    const select = el("select");
    for (const option of spec.options ?? []) {
      const node = el("option", undefined, option);
      node.value = option;
      select.append(node);
    }
    select.value = String(spec.value);
    input = select;
  } else {
    const node = el("input");
    node.type = spec.type === "datalist" ? "text" : spec.type;
    if (spec.type === "datalist") {
      // A text box with a menu attached: click for the known ones, type for
      // anything else. Neither a select nor a plain box does both.
      const list = el("datalist");
      list.id = `${id}-options`;
      for (const option of spec.options ?? []) {
        const node2 = el("option");
        node2.value = option;
        list.append(node2);
      }
      node.setAttribute("list", list.id);
      wrap.append(list);
    }
    if (spec.min !== undefined) node.min = String(spec.min);
    if (spec.max !== undefined) node.max = String(spec.max);
    if (spec.step !== undefined) node.step = String(spec.step);
    if (spec.type === "checkbox") node.checked = Boolean(spec.value);
    else node.value = String(spec.value);
    input = node;
  }
  input.id = id;

  wrap.append(label, input);

  if (spec.type === "range") {
    const readout = el("span", "readout", String(spec.value));
    input.addEventListener("input", () => { readout.textContent = (input as HTMLInputElement).value; });
    wrap.append(readout);
  }
  if (spec.hint) wrap.append(el("p", "hint", spec.hint));
  return wrap;
}

function readValue(id: string, spec: FieldSpec): string | number | boolean {
  const node = document.getElementById(id) as HTMLInputElement | HTMLSelectElement;
  if (spec.type === "checkbox") return (node as HTMLInputElement).checked;
  if (spec.type === "number" || spec.type === "range") return Number(node.value);
  return node.value;
}

// ---- reading the form back into a piece -------------------------------------

function currentPiece(): Piece {
  const piece: Record<string, unknown> = {};
  for (const spec of PIECE_FIELDS) piece[spec.key] = readValue(`piece-${spec.key}`, spec);

  // Two pickers, one field. The renderer wants "D minor"; asking someone to
  // type that is asking them to get it wrong.
  piece.key = `${piece.tonic} ${piece.mode}`;
  delete piece.tonic;
  delete piece.mode;
  piece.barsPerChord = Number(piece.barsPerChord);

  const layers: Record<string, unknown> = {};
  for (const role of ROLES) {
    const enabled = (document.getElementById(`enable-${role}`) as HTMLInputElement).checked;
    if (!enabled) continue;
    const spec: Record<string, unknown> = {};
    for (const field of layerFields(role)) {
      spec[field.key] = readValue(`${role}-${field.key}`, field);
    }
    layers[role] = spec;
  }

  // A hand-edited beat is not a form field, so it is merged back in here. The
  // form stays the source of truth for everything it actually holds.
  if (beatEdit && layers.perc) {
    (layers.perc as Record<string, unknown>).pattern = beatEdit;
  }

  piece.layers = layers;
  return piece as unknown as Piece;
}

/**
 * Write a generated piece into the form.
 *
 * The form stays the single source of truth even when a roll produced the
 * piece, so opening Fine tune after Surprise me shows what is actually
 * playing rather than the defaults it started from.
 */
function showPiece(piece: Piece): void {
  const write = (id: string, spec: FieldSpec, value: unknown) => {
    const node = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
    if (!node || value === undefined) return;
    if (spec.type === "checkbox") (node as HTMLInputElement).checked = Boolean(value);
    else node.value = String(value);
    // Range controls carry their own number beside them, and it is a sibling
    // rather than bound, so it has to be told.
    const readout = node.parentElement?.querySelector(".readout");
    if (readout && spec.type === "range") readout.textContent = String(value);
  };

  const [tonic, mode] = piece.key.trim().split(/\s+/);
  const values: Record<string, unknown> = {
    ...(piece as unknown as Record<string, unknown>),
    tonic,
    mode: mode ?? "major",
  };
  for (const spec of PIECE_FIELDS) write(`piece-${spec.key}`, spec, values[spec.key]);
  for (const role of ROLES) {
    const layer = piece.layers[role] as Record<string, unknown> | undefined;
    (document.getElementById(`enable-${role}`) as HTMLInputElement).checked = Boolean(layer);
    if (!layer) continue;
    for (const field of layerFields(role)) write(`${role}-${field.key}`, field, layer[field.key]);
  }
  updateTiming();
}

/** One line describing what is loaded, for people who never open Fine tune. */
function updateSummary(): void {
  const box = document.getElementById("summary")!;
  try {
    const piece = currentPiece();
    const { duration } = describePiece(piece);
    const layers = ROLES.filter((r) => piece.layers[r]).join(", ");
    box.innerHTML =
      `<strong>${escapeHtml(piece.key)}</strong>` +
      `<span class="muted"> · ${piece.tempo} bpm · ${escapeHtml(piece.progression)}` +
      ` · ${duration.toFixed(0)}s · ${layers}</span>`;
  } catch {
    box.textContent = "";
  }
}

function escapeHtml(text: string): string {
  const node = document.createElement("div");
  node.textContent = text;
  return node.innerHTML;
}

// ---- the live timing readout ------------------------------------------------

/**
 * Say what the piece will actually render as, before anyone waits for it.
 *
 * The snap to whole cycles is not a rounding detail. Four chords of four bars
 * at 82bpm is a 47 second cycle, so asking for 72 seconds gets 94, and someone
 * who does not know that will think the length control is broken.
 */
function updateTiming(): void {
  const box = document.getElementById("timing")!;
  const warn = document.getElementById("timing-warning")!;
  try {
    const { cycleSeconds, duration, cycles, melodySilent } = describePiece(currentPiece());
    box.textContent =
      `renders as ${duration.toFixed(1)}s, ` +
      `${cycles.toFixed(cycles % 1 === 0 ? 0 : 2)} × ${cycleSeconds.toFixed(1)}s cycle`;
    box.classList.remove("bad");
    warn.textContent = melodySilent
      ? "Under two cycles, so the melody never enters. It starts on the second pass."
      : "";
  } catch (error) {
    box.textContent = String(error instanceof Error ? error.message : error);
    box.classList.add("bad");
    warn.textContent = "";
  }
  updateSummary();
}

// ---- rendering --------------------------------------------------------------

const player = new AdaptivePlayer();
let pack: { manifest: Record<string, unknown>; stems: { role: string; samples: Float64Array }[]; mixdown: Float64Array } | null = null;
let worker: Worker | null = null;

function status(message: string, kind: "" | "busy" | "bad" = ""): void {
  const node = document.getElementById("status")!;
  node.textContent = message;
  node.className = kind;
}

/** Buttons that must not be pressed while a render is in flight. */
const BUSY_BUTTONS = ["render", "surprise", "reroll"];

/** Rising id, so a reply that has been overtaken can be dropped. */
let requestId = 0;
let pendingPlay = false;
/** What the worker last rendered, for working out what has changed since. */
let lastRendered: Piece | null = null;

function setBusyButtons(busy: boolean): void {
  for (const id of BUSY_BUTTONS) {
    const button = document.getElementById(id) as HTMLButtonElement | null;
    if (button) button.disabled = busy;
  }
}

/** Was it playing when this render started? If so, keep playing afterwards. */
let wasPlaying = false;

/** Has anything actually been heard yet? Decides whether Play is marked. */
let everPlayed = false;
/** Has the "press play" instruction been shown? It is for the first render only. */
let toldToPlay = false;

async function startPlaying(): Promise<void> {
  const play = document.getElementById("play") as HTMLButtonElement;
  try {
    await player.play();
    play.textContent = "Stop";
    everPlayed = true;
    play.classList.remove("btn-ready");
  } catch {
    play.textContent = "Play";
  }
}

function stopPlaying(): void {
  player.stop();
  (document.getElementById("play") as HTMLButtonElement).textContent = "Play";
}

function render(thenPlay = false, because = "Pressing a track"): void {
  document.getElementById("press-label")!.textContent = because;
  let piece: Piece;
  try {
    piece = currentPiece();
  } catch (error) {
    status(String(error), "bad");
    return;
  }

  // Work out how little has to be redone. The worker keeps the previous
  // layers, so changing one thing costs one layer rather than five.
  const changed = changedLayers(lastRendered, piece);
  if (pack && !needsRender(changed)) {
    // Renaming changes no audio. Re-rendering ninety seconds because someone
    // typed a letter is exactly the wait that makes a tool feel heavy.
    //
    // Said out loud, because a button that does nothing and reports nothing
    // reads as broken rather than as already up to date.
    updateSummary();
    status("Nothing to redo, this is already what you are hearing.");
    return;
  }

  // The worker is kept alive rather than replaced, because its cache of raw
  // layers is the entire point. A stale reply is ignored by its id instead.
  if (!worker) {
    worker = new Worker(new URL("./worker.js", import.meta.url), { type: "module" });
    worker.onmessage = onRendered;
    worker.onerror = (event) => {
      setBusyButtons(false);
      document.getElementById("press")!.setAttribute("hidden", "");
      status(event.message || "the renderer failed", "bad");
    };
  }

  setBusyButtons(true);
  document.getElementById("press")!.removeAttribute("hidden");
  status("", "busy");

  requestId += 1;
  pendingPlay = thenPlay;
  lastRendered = piece;
  worker.postMessage({
    id: requestId,
    piece,
    only:
      changed === "everything" ? undefined
      : changed === "finish" ? []
      : changed,
  });
}

/**
 * Take a finished render.
 *
 * Module level rather than a closure, because the worker is now kept alive
 * between renders and its handler is set once. Replies carry the id they were
 * asked for, so a slow reply that has been overtaken is dropped rather than
 * overwriting a newer one.
 */
function onRendered(event: MessageEvent<RenderResponse>): void {
  const data = event.data;
  if (data.id !== requestId) return;

  setBusyButtons(false);
  document.getElementById("press")!.setAttribute("hidden", "");

  {
    if (!data.ok) {
      status(data.error, "bad");
      return;
    }
    pack = {
      manifest: data.manifest as Record<string, unknown>,
      stems: data.stems,
      mixdown: data.mixdown,
    };
    const manifest = pack.manifest as {
      layers: { role: string; enterAt: number }[];
      loop: boolean; duration: number; barSeconds: number;
    };
    barSeconds = manifest.barSeconds;
    loopSeconds = manifest.duration;
    player.load(
      data.stems.map((stem) => ({
        role: stem.role,
        enterAt: manifest.layers.find((l) => l.role === stem.role)?.enterAt ?? 0,
        samples: stem.samples,
      })),
      manifest.loop,
    );
    // Peak is reported because a render that arrives silent or truncated is
    // otherwise indistinguishable from a speaker problem, and the user blames
    // their speakers. It should always read 0.65: that is what the mixdown was
    // normalised to, so anything else means the audio did not survive the trip
    // back from the worker.
    let peak = 0;
    for (const s of data.mixdown) peak = Math.max(peak, Math.abs(s));
    // Says how many layers were actually synthesised, because "0.8s" after a
    // string of "4.4s" otherwise looks like something went wrong.
    const how = data.synthesised < 5 ? `, ${data.synthesised} of 5 layers` : "";
    const detail =
      `rendered ${manifest.duration.toFixed(1)}s in ${(data.elapsedMs / 1000).toFixed(1)}s` +
      `${how}, peak ${peak.toFixed(3)}`;
    // The instruction is for the very first render only. Leaving it up until
    // something has been PLAYED meant every later render reported nothing, so
    // editing the beat before pressing play looked like it did nothing at all.
    status(toldToPlay ? detail : "Ready. Press play, then drag the pressure.",
      peak > 0 ? "" : "bad");
    toldToPlay = true;
    document.getElementById("export")!.removeAttribute("disabled");
    document.getElementById("play")!.removeAttribute("disabled");
    document.getElementById("share")!.removeAttribute("disabled");
    document.getElementById("listen")!.removeAttribute("hidden");
    // Marked until something has been heard, so the eye goes to the button that
    // does the thing this page is for. Two loud buttons would just be noise, so
    // this is an outline rather than a second filled one.
    if (!everPlayed) document.getElementById("play")!.classList.add("btn-ready");
    updateLink();
    buildMeters();
    paintMeters();
    paintGroove(currentPiece());
    refreshKeepButton();
    refreshVerbs();

    // Lanes follow the stack's order, so the two diagrams line up row for row
    // and the eye can move between them without re-reading the labels.
    const order = new Map(Array.from(document.querySelectorAll(".lane-label")).map((t, i) => [
      (t.id || "").replace("meter-", ""), i,
    ]));
    timeline?.load(
      [...data.stems]
        .sort((a, b) => (order.get(a.role) ?? 99) - (order.get(b.role) ?? 99))
        .map((s) => ({ role: s.role, samples: s.samples, colour: laneColour(s.role) })),
    );

    // Carry on playing across a re-roll, and start on request. Autoplay is
    // allowed here because a click began this, but a browser can still refuse,
    // so a failure leaves the button as the way in rather than an error.
    if (pendingPlay || wasPlaying) void startPlaying();
    wasPlaying = false;
    pendingPlay = false;
  }
}

// ---- meters -----------------------------------------------------------------

/**
 * Build the lane labels and the threshold ticks.
 *
 * Rows are ordered by the pressure each layer arrives at rather than by mix
 * order, because that order is the thing worth reading. A list with melody
 * above pad would be a lie about the piece.
 *
 * Label heights come from the same constants the canvas draws with, so the two
 * columns line up without either measuring the other.
 */
function buildMeters(): void {
  const labels = document.getElementById("meters")!;
  const ticks = document.getElementById("pressure-ticks")!;
  labels.replaceChildren();
  ticks.replaceChildren();

  const rows = player.levels()
    .map((l) => ({ ...l, enterAt: player.enterAtFor(l.role) }))
    .sort((a, b) => a.enterAt - b.enterAt);

  for (const { role, enterAt } of rows) {
    const label = el("div", "lane-label");
    label.id = `meter-${role}`;
    label.style.height = `${LANE_HEIGHT}px`;
    label.style.marginBottom = `${LANE_GAP}px`;
    label.append(el("span", undefined, role));
    label.append(el("b", undefined, enterAt > 0 ? `from ${enterAt.toFixed(2)}` : "always"));
    labels.append(label);

    // Layers that are always on would put every tick at zero on top of each
    // other, saying nothing. The label already says "always".
    if (enterAt <= 0) continue;
    const tick = el("div", "tick");
    tick.style.left = `${enterAt * 100}%`;
    tick.style.setProperty("--ink", `var(--${role})`);
    tick.dataset.role = role;
    tick.title = `${role} arrives at ${enterAt.toFixed(2)}`;
    ticks.append(tick);
  }

  labels.classList.remove("fresh");
  // Restarting a CSS animation needs the class off, a reflow, then on again.
  void labels.offsetWidth;
  labels.classList.add("fresh");
}

/**
 * Draw the layer meters.
 *
 * Called when the intensity changes rather than left to the animation frame,
 * because `requestAnimationFrame` stops entirely while a tab is hidden. A
 * slider that has moved should show the new mix the moment it moves, and it
 * should still be showing the right thing when someone switches back to the
 * tab rather than whatever it happened to be when they left.
 */
function paintMeters(): void {
  const intensity = Number((document.getElementById("intensity") as HTMLInputElement).value);

  for (const { role, level } of player.levels()) {
    const label = document.getElementById(`meter-${role}`);
    label?.classList.toggle("lit", level > 0.02);
    const tick = document.querySelector(`.tick[data-role="${role}"]`) as HTMLElement | null;
    if (tick) tick.dataset.lit = level > 0.02 ? "yes" : "no";
  }

  const fill = document.getElementById("pressure-fill") as HTMLElement | null;
  const head = document.getElementById("pressure") as HTMLElement | null;
  if (fill) fill.style.width = `${intensity * 100}%`;
  if (head) head.style.left = `${intensity * 100}%`;

  document.getElementById("stack")?.setAttribute("aria-valuenow", intensity.toFixed(2));
  document.getElementById("intensity-readout")!.textContent = intensity.toFixed(2);
}

/**
 * The beat, editable.
 *
 * Drums are the one layer Hum does not invent, because a random drum pattern is
 * not a groove. That makes this the one place where typing it out by hand is
 * the right interface rather than a fallback, so the printed grid is also the
 * control: click a step to cycle it through rest, hit and accent.
 *
 * Edits live on the piece as an inline `pattern`, not by mutating the shared
 * groove, so nothing that anyone else rolls is affected by what you clicked.
 */
let beatEdit: Groove | null = null;
let beatTimer: number | undefined;

function currentGroove(piece: Piece): Groove | undefined {
  const perc = piece.layers.perc;
  if (!perc) return undefined;
  return perc.pattern ?? (perc.groove ? GROOVES[perc.groove] : undefined);
}

function paintGroove(piece: Piece): void {
  const box = document.getElementById("groove")!;
  const grid = document.getElementById("groove-grid")!;
  const groove = currentGroove(piece);

  if (!groove || !groove.voices.length) {
    box.setAttribute("hidden", "");
    return;
  }

  grid.replaceChildren();
  stepsPerBar = groove.stepsPerBar ?? 16;

  groove.voices.forEach((voice, voiceIndex) => {
    const row = el("div", "groove-row");
    row.append(el("span", undefined, voice.drum));

    const steps = el("div", "steps");
    [...voice.steps].forEach((mark, stepIndex) => {
      const cell = el("button", "step");
      cell.type = "button";
      // Rests are middle dots rather than full stops, which sit on the baseline
      // and make a pattern read as a sentence trailing off.
      cell.textContent = mark === "." ? "·" : mark;
      cell.dataset.mark = mark;
      cell.dataset.step = String(stepIndex);
      cell.setAttribute(
        "aria-label",
        `${voice.drum}, step ${stepIndex + 1}: ${mark === "X" ? "accent" : mark === "x" ? "hit" : "rest"}`,
      );
      cell.addEventListener("click", () => cycleStep(voiceIndex, stepIndex));
      steps.append(cell);
    });

    row.append(steps);
    grid.append(row);
  });

  box.removeAttribute("hidden");
}

/** rest → hit → accent → rest. Three states because accents are what stop a
 *  repeated pattern sounding like a machine. */
function cycleStep(voiceIndex: number, stepIndex: number): void {
  const groove = currentGroove(currentPiece());
  if (!groove) return;

  const next: Record<string, string> = { ".": "x", x: "X", X: "." };
  // Copied rather than edited in place, so the shared groove stays the groove.
  const edited: Groove = {
    ...groove,
    voices: groove.voices.map((voice, i) => {
      if (i !== voiceIndex) return { ...voice };
      const steps = [...voice.steps];
      steps[stepIndex] = next[steps[stepIndex]] ?? "x";
      return { ...voice, steps: steps.join("") };
    }),
  };

  beatEdit = edited;
  paintGroove(currentPiece());

  // Rendering takes seconds, so a click does not trigger one. Tapping out a
  // whole bar would otherwise queue a dozen renders and throw away eleven.
  window.clearTimeout(beatTimer);
  // Starts playing if it was not already. Tapping a drum grid and hearing
  // nothing is the whole complaint: there is no way to judge an edit you cannot
  // hear, and the tap is a real gesture so the browser allows the audio.
  beatTimer = window.setTimeout(() => render(true, "Laying the new beat down"), 900);
}

/**
 * Make the stack the control.
 *
 * The slider input still exists and is still the value everything reads, it is
 * just hidden. Dragging writes to it and fires the same event, so there is one
 * path into the player rather than two that can disagree.
 */
function wireStack(): void {
  const stack = document.getElementById("stack")!;
  const input = document.getElementById("intensity") as HTMLInputElement;

  const setFromX = (clientX: number) => {
    const track = document.querySelector(".pressure-track") as HTMLElement | null;
    if (!track) return;
    const box = track.getBoundingClientRect();
    const value = Math.min(1, Math.max(0, (clientX - box.left) / box.width));
    input.value = value.toFixed(2);
    input.dispatchEvent(new Event("input", { bubbles: true }));
  };

  let dragging = false;
  stack.addEventListener("pointerdown", (event) => {
    dragging = true;
    stack.setPointerCapture(event.pointerId);
    setFromX(event.clientX);
  });
  stack.addEventListener("pointermove", (event) => {
    if (dragging) setFromX(event.clientX);
  });
  const release = () => { dragging = false; };
  stack.addEventListener("pointerup", release);
  stack.addEventListener("pointercancel", release);

  stack.addEventListener("keydown", (event) => {
    const step = event.shiftKey ? 0.1 : 0.02;
    const current = Number(input.value);
    let next = current;
    if (event.key === "ArrowRight" || event.key === "ArrowUp") next = current + step;
    else if (event.key === "ArrowLeft" || event.key === "ArrowDown") next = current - step;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = 1;
    else return;
    event.preventDefault();
    input.value = Math.min(1, Math.max(0, next)).toFixed(2);
    input.dispatchEvent(new Event("input", { bubbles: true }));
  });
}

/** Bar length and step count of what is loaded, for the playhead. */
let barSeconds = 0;
let stepsPerBar = 16;
let litStep = -1;
let loopSeconds = 0;
let timeline: Timeline | null = null;

/** The ink each layer is drawn in, read from the stylesheet so there is one
 *  copy of the palette rather than one here and one in the CSS. */
function laneColour(role: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(`--${role}`).trim();
  return value || "#5D74FF";
}

/**
 * Run a playhead across the beat grid.
 *
 * The position comes from the audio clock rather than a timer started
 * alongside it, for the same reason the game reads it that way: a timer drifts
 * out of the music, and a playhead that is nearly right is worse than none.
 *
 * Only touches the DOM when the step changes. At sixteen steps and 140bpm that
 * is about nine times a second rather than sixty.
 */
function tick(): void {
  if (timeline && loopSeconds > 0) {
    const levels = new Map(player.levels().map((l) => [l.role, l.level]));
    timeline.draw(player.playing ? player.position() : 0, loopSeconds, levels, barSeconds);
  }

  if (player.playing && barSeconds > 0) {
    const intoBar = player.position() % barSeconds;
    const step = Math.floor((intoBar / barSeconds) * stepsPerBar) % stepsPerBar;
    if (step !== litStep) {
      document.querySelectorAll(".step.now").forEach((c) => c.classList.remove("now"));
      document.querySelectorAll(`.step[data-step="${step}"]`).forEach((c) => c.classList.add("now"));
      litStep = step;
    }
  } else if (litStep !== -1) {
    document.querySelectorAll(".step.now").forEach((c) => c.classList.remove("now"));
    litStep = -1;
  }
  requestAnimationFrame(tick);
}

// ---- export -----------------------------------------------------------------

function download(name: string, bytes: Uint8Array, type: string): void {
  const blob = new Blob([bytes as unknown as BlobPart], { type });
  const url = URL.createObjectURL(blob);
  const link = el("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

function exportPack(): void {
  if (!pack) return;
  const manifest = pack.manifest as {
    name: string; key: string; tempo: number; seed: number;
  };
  const piece = currentPiece();
  const encode = (text: string) => new TextEncoder().encode(text);

  const entries = [
    ...pack.stems.map((stem) => ({ name: `${stem.role}.wav`, bytes: encodeWav(stem.samples) })),
    { name: "_mixdown.wav", bytes: encodeWav(pack.mixdown) },
    { name: "hum.json", bytes: encode(JSON.stringify(pack.manifest, null, 2)) },
    // Named so it sorts to the top of the folder and is the first thing anyone
    // opening the zip sees. A licence nobody notices is a licence nobody keeps.
    {
      name: "READ ME AND LICENCE.txt",
      bytes: encode(licenceText({
        name: manifest.name,
        key: piece.key,
        tempo: piece.tempo,
        progression: piece.progression,
        seed: piece.seed,
        link: location.href,
      })),
    },
  ];
  download(`${manifest.name || "piece"}.zip`, makeZip(entries), "application/zip");
  status(`Exported. It carries a CC BY licence: credit "${creditLine()}".`);
}

// ---- moods ------------------------------------------------------------------

/**
 * What to roll. null means let it decide, a string is a built-in, an object is
 * a feeling someone invented.
 */
let chosenMood: string | Mood | null = null;
let myFeelings: Mood[] = [];

const newSeed = (): number => 1 + Math.floor(Math.random() * 999999);

const moodName = (mood: string | Mood | null): string | null =>
  mood === null ? null : typeof mood === "string" ? mood : mood.name;

/**
 * Build the feeling chips, in named rows.
 *
 * Seventeen chips in one block is a wall nobody reads. Split into calm and
 * moving it becomes a choice between two kinds of music, and that split is also
 * the most useful thing you can say about them.
 */
function buildMoods(): void {
  const calm = document.getElementById("moods-calm")!;
  const moving = document.getElementById("moods-moving")!;
  const own = document.getElementById("moods-mine")!;
  const ownRow = document.getElementById("mine-row")!;
  for (const box of [calm, moving, own]) box.replaceChildren();

  const select = (mood: string | Mood | null, chip: HTMLElement) => {
    chosenMood = mood;
    for (const other of Array.from(document.querySelectorAll(".mood"))) {
      other.setAttribute("aria-pressed", String(other === chip));
    }
  };

  const chipFor = (mood: string | Mood | null, mine = false) => {
    const chip = el("button", mine ? "mood mine" : "mood", moodName(mood) ?? "anything");
    chip.type = "button";
    chip.setAttribute("aria-pressed", String(moodName(mood) === moodName(chosenMood)));
    chip.addEventListener("click", () => select(mood, chip));
    return chip;
  };

  // "Anything" joins the calm row rather than getting one of its own, because
  // a label above a single chip is a label saying nothing.
  calm.append(chipFor(null));
  for (const name of MOOD_FAMILIES.calm) calm.append(chipFor(name));
  for (const name of MOOD_FAMILIES.moving) moving.append(chipFor(name));

  if (!myFeelings.length) {
    own.append(el("span", "mine-empty", "none yet"));
  }
  for (const feeling of myFeelings) {
    own.append(chipFor(feeling, true));

    // Paired with its own chip rather than hidden behind an edit mode, because
    // a handful of styles someone made is not a list that needs managing.
    const drop = el("button", "mood-drop", "×");
    drop.type = "button";
    drop.title = `Forget "${feeling.name}"`;
    drop.setAttribute("aria-label", `Forget the feeling ${feeling.name}`);
    drop.addEventListener("click", () => {
      myFeelings = deleteFeeling(feeling.name);
      if (moodName(chosenMood) === feeling.name) chosenMood = null;
      buildMoods();
    });
    own.append(drop);
  }

  // The way to make one lives with the ones you have, not in the settings
  // drawer. Disabled until something has been rendered, because a feeling is
  // taken from the piece that is loaded and there is nothing to take otherwise.
  const add = el("button", "mood add", "+ make one");
  add.type = "button";
  add.id = "keep-open";
  add.addEventListener("click", openKeep);
  own.append(add);
  refreshKeepButton();
}

/**
 * Enable "make one" once there is something to make it from.
 *
 * Called after every render as well as when the chips are rebuilt, because the
 * chips are built at startup when nothing has been rendered yet and would
 * otherwise stay disabled for the whole session.
 */
function refreshKeepButton(): void {
  const add = document.getElementById("keep-open") as HTMLButtonElement | null;
  if (!add) return;
  add.disabled = !pack;
  add.title = pack
    ? "Save what is set up now as a feeling of your own"
    : "Roll a track first, then this saves it as a feeling";
}

// ---- links ------------------------------------------------------------------

/**
 * Keep the address bar showing what is playing.
 *
 * `replaceState` rather than `pushState`: every tweak would otherwise become a
 * back-button step, and getting out of the tool would mean pressing back forty
 * times. The address bar being correct is worth having on its own, because
 * copying it is what people do without being told there is a button.
 */
function updateLink(): void {
  if (!pack) return;
  try {
    const fragment = encodePiece(currentPiece(), moodName(chosenMood) ?? undefined);
    history.replaceState(null, "", `#${fragment}`);
  } catch {
    // A piece that cannot be encoded is not worth interrupting anyone over.
  }
}

async function copyLink(): Promise<void> {
  updateLink();
  const note = document.getElementById("share") as HTMLButtonElement;
  try {
    await navigator.clipboard.writeText(location.href);
    status(`Link copied. It carries the whole track, ${location.href.length} characters.`);
  } catch {
    // Clipboard access can be refused outright. Selecting the address bar is
    // then the answer, and saying so beats a silent failure.
    status("Could not reach the clipboard. The address bar has the link.", "bad");
  }
  note.blur();
}

/** Load a piece someone was sent, if the address has one. */
function openSharedLink(): boolean {
  const shared = decodePiece(location.hash);
  if (!shared) return false;

  if (shared.mood) {
    chosenMood = shared.mood;
    buildMoods();
  }
  showPiece(shared.piece);
  render(false, "Opening a shared track");
  return true;
}

// ---- the verbs --------------------------------------------------------------

/** Paired so each control reads as an axis rather than ten loose buttons. */
const VERB_PAIRS: [Verb, string, Verb, string][] = [
  ["calmer", "calmer", "busier", "busier"],
  ["darker", "darker", "brighter", "brighter"],
  ["slower", "slower", "faster", "faster"],
  ["lessBeat", "less beat", "moreBeat", "more beat"],
  ["lower", "lower", "higher", "higher"],
];

let verbTimer: number | undefined;

function buildVerbs(): void {
  const box = document.getElementById("verbs")!;
  box.replaceChildren();

  for (const [leftVerb, leftText, rightVerb, rightText] of VERB_PAIRS) {
    const pair = el("div", "verb-pair");
    for (const [verb, text] of [[leftVerb, leftText], [rightVerb, rightText]] as const) {
      const button = el("button", "verb", text);
      button.type = "button";
      button.dataset.verb = verb;
      button.addEventListener("click", () => applyVerb(verb));
      pair.append(button);
    }
    box.append(pair);
  }
  refreshVerbs();
}

/**
 * Grey out a verb that has run out of road.
 *
 * A button that does nothing when pressed reads as the tool being broken,
 * rather than as having reached the end of the range.
 */
function refreshVerbs(): void {
  let piece: Piece | null = null;
  try {
    piece = currentPiece();
  } catch {
    piece = null;
  }
  for (const button of Array.from(document.querySelectorAll<HTMLButtonElement>(".verb"))) {
    const verb = button.dataset.verb as Verb | undefined;
    button.disabled = !piece || !verb || !canNudge(piece, verb);
  }
}

/**
 * Push the piece in a direction and hear it.
 *
 * Debounced, because pressing "busier" three times quickly should be one
 * render of the result rather than three renders of the way there.
 */
function applyVerb(verb: Verb): void {
  let piece: Piece;
  try {
    piece = nudge(currentPiece(), verb);
  } catch (error) {
    status(String(error instanceof Error ? error.message : error), "bad");
    return;
  }

  // A verb can turn percussion off entirely, and the beat someone hand-edited
  // belonged to the groove it came from.
  if (!piece.layers.perc) beatEdit = null;

  showPiece(piece);
  refreshVerbs();

  window.clearTimeout(verbTimer);
  verbTimer = window.setTimeout(() => render(player.playing, "Reshaping"), 450);
}

/** Open the make-a-feeling dialog, showing what it is about to capture. */
function openKeep(): void {
  const dialog = document.getElementById("keep") as HTMLDialogElement;
  const current = document.getElementById("keep-current")!;
  const note = document.getElementById("feeling-note")!;

  note.textContent = "";
  note.className = "keep-note";
  try {
    const piece = currentPiece();
    const groove = piece.layers.perc?.groove;
    current.textContent =
      `${piece.key} · ${piece.tempo} bpm · ${piece.progression} · ` +
      `${piece.barsPerChord ?? 4} bars a chord · ${groove ? `${groove} beat` : "no drums"}`;
  } catch {
    current.textContent = "";
  }

  dialog.showModal();
  (document.getElementById("feeling-name") as HTMLInputElement).focus();
}

/** Save whatever is currently set up as a style that can be rolled again. */
function keepFeeling(): void {
  const input = document.getElementById("feeling-name") as HTMLInputElement;
  const note = document.getElementById("feeling-note")!;
  const taken = [...MOOD_NAMES, ...myFeelings.map((f) => f.name)].map((n) => n.toLowerCase());

  const name = cleanName(input.value, taken);
  if (!name) {
    note.textContent = input.value.trim()
      ? "That name is already taken, or has nothing in it."
      : "Give it a name first.";
    note.className = "keep-note bad";
    return;
  }

  try {
    const piece = currentPiece();
    describePiece(piece);
    myFeelings = saveFeeling(moodFromPiece(piece, name));
  } catch (error) {
    note.textContent = `Cannot save this: ${error instanceof Error ? error.message : error}`;
    note.className = "keep-note bad";
    return;
  }

  input.value = "";
  buildMoods();
  (document.getElementById("keep") as HTMLDialogElement).close();

  // Said on the page rather than in the dialog that just closed, so there is
  // something to read after it goes.
  status(`Saved "${name}". It is in the Yours row now.`);
}

// ---- wiring -----------------------------------------------------------------

function build(): void {
  const pieceBox = document.getElementById("piece-fields")!;
  for (const spec of PIECE_FIELDS) pieceBox.append(control(`piece-${spec.key}`, spec));

  const layerBox = document.getElementById("layer-fields")!;
  for (const role of ROLES) {
    const card = el("section", "layer");
    const head = el("div", "layer-head");
    const toggle = el("input");
    toggle.type = "checkbox";
    toggle.id = `enable-${role}`;
    toggle.checked = true;
    const name = el("label", "layer-name", role);
    name.htmlFor = `enable-${role}`;
    head.append(toggle, name);
    card.append(head);
    for (const field of layerFields(role)) card.append(control(`${role}-${field.key}`, field));
    layerBox.append(card);
  }

  myFeelings = loadFeelings();
  buildMoods();
  buildVerbs();
  document.getElementById("feeling-save")!.addEventListener("click", keepFeeling);
  document.getElementById("keep-cancel")!.addEventListener("click", () => {
    (document.getElementById("keep") as HTMLDialogElement).close();
  });
  document.getElementById("feeling-name")!.addEventListener("keydown", (event) => {
    if ((event as KeyboardEvent).key === "Enter") keepFeeling();
  });

  // Built once at startup rather than on open, so the first click is instant.
  buildHelp();
  const help = document.getElementById("help") as HTMLDialogElement;
  document.getElementById("help-open")!.addEventListener("click", () => help.showModal());
  document.getElementById("help-close")!.addEventListener("click", () => help.close());

  /**
   * Show the reference once, to whoever has not seen it.
   *
   * Nothing on this page explains itself from its controls alone: "pressure",
   * "figures" and a grid of X and dots are not guessable. Showing it once beats
   * a first visit spent guessing, and showing it every time would be rude.
   *
   * localStorage can throw outright in some privacy modes, and the page working
   * matters more than the note, so a failure just means it shows again.
   */
  const SEEN = "hum.reference-seen";
  const remember = (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // No storage available. Nothing here is worth breaking the page for.
    }
  };
  let seen = false;
  try {
    seen = localStorage.getItem(SEEN) === "1";
  } catch {
    seen = false;
  }
  // Marked as seen when it is SHOWN, not when it is closed. The intent is
  // "show this once", which is already satisfied the moment it opens, and
  // hanging it off the dialog's `close` event turned out not to fire reliably.
  // Fewer moving parts, and the failure mode is a second viewing rather than a
  // modal that returns forever.
  if (!seen) {
    help.showModal();
    remember(SEEN, "1");
  }
  // Clicking the backdrop closes it. The dialog element itself fills the whole
  // viewport for hit-testing, so the check is whether the click landed outside
  // the box rather than on which element it hit.
  help.addEventListener("click", (event) => {
    const box = help.getBoundingClientRect();
    const outside =
      event.clientX < box.left || event.clientX > box.right ||
      event.clientY < box.top || event.clientY > box.bottom;
    if (outside) help.close();
  });

  document.getElementById("render")!.addEventListener("click", () => render());
  document.getElementById("export")!.addEventListener("click", exportPack);
  document.getElementById("share")!.addEventListener("click", () => void copyLink());

  // A whole new piece: key, mode, tempo, chords, instruments, the lot. Rolling
  // only the seed barely changes anything, because pad and bass come straight
  // from the key and progression and carry no randomness at all.
  document.getElementById("surprise")!.addEventListener("click", () => {
    wasPlaying = player.playing;
    stopPlaying();
    // A new piece gets the new piece's beat. Carrying an edit across would
    // apply a hand-written pattern to a groove nobody chose.
    beatEdit = null;
    showPiece(randomPiece(newSeed(), { mood: chosenMood ?? undefined }));
    render(true);
  });

  // The same piece with different figures. Small on purpose: it is the "not
  // quite" button, not the "something else" button.
  document.getElementById("reroll")!.addEventListener("click", () => {
    wasPlaying = player.playing;
    stopPlaying();
    (document.getElementById("piece-seed") as HTMLInputElement).value = String(newSeed());
    updateTiming();
    render(true);
  });

  const play = document.getElementById("play") as HTMLButtonElement;
  play.addEventListener("click", () => {
    if (player.playing) stopPlaying();
    else void startPlaying();
  });

  const intensity = document.getElementById("intensity") as HTMLInputElement;
  intensity.addEventListener("input", () => {
    player.setIntensity(Number(intensity.value));
    paintMeters();
  });
  wireStack();

  for (const spec of PIECE_FIELDS) {
    document.getElementById(`piece-${spec.key}`)!.addEventListener("input", updateTiming);
  }
  for (const role of ROLES) {
    document.getElementById(`enable-${role}`)!.addEventListener("input", updateTiming);
  }

  const canvas = document.getElementById("timeline") as HTMLCanvasElement | null;
  if (canvas) {
    timeline = new Timeline(canvas);
    // The peaks are reduced to the drawing width, so a resize needs a redraw
    // rather than a stretch. Debounced because dragging a window edge fires
    // this constantly and rebuilding is not free.
    let resizeTimer: number | undefined;
    window.addEventListener("resize", () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (!pack) return;
        timeline?.load(pack.stems.map((s) => ({
          role: s.role, samples: s.samples, colour: laneColour(s.role),
        })));
      }, 200);
    });
  }

  updateTiming();
  tick();

  // A shared link is what someone came here for, so it wins over the welcome.
  // The reference can wait for the `?` button.
  if (openSharedLink()) {
    help.close();
  } else {
    // Otherwise arrive with a track already made. An empty page with three
    // buttons on it does not say what this is, and the first thing anyone would
    // have pressed is the one that fills it in.
    //
    // The render starts underneath the reference on a first visit, so the four
    // second wait happens while there is something to read, and the track is
    // ready by the time the modal is closed.
    chosenMood = null;
    showPiece(randomPiece(newSeed()));
    render(false, "Making you something to start with");
  }

  // Pasting a link while the tool is already open changes the fragment without
  // reloading anything, so without this it would appear to do nothing at all.
  // `replaceState` does not fire this, so keeping the address bar current
  // cannot set it off.
  window.addEventListener("hashchange", () => {
    stopPlaying();
    openSharedLink();
  });
}

build();
